<div>
  @if (duplicateIndicators.length === 0) {
    <div class="text-center p-5">
      <i class="pi pi-check-circle text-5xl text-green-500 mb-3"></i>
      <h3 class="text-xl">Keine Probleme gefunden</h3>
      <p class="text-color-secondary">
        Alle Indikatoren sind eindeutig. Jeder Schlüssel ist nur einmal im
        System vorhanden.
      </p>
    </div>
  } @else {
    <!-- Filter and Action Bar -->
    <div
      class="flex justify-content-between align-items-center mb-4 gap-3 py-1">
      <div class="flex flex-column gap-2">
        <span class="text-sm font-semibold">Filter</span>
        <p-selectButton
          [options]="filterOptions"
          [(ngModel)]="filterState"
          optionLabel="label"
          optionValue="value"></p-selectButton>
      </div>

      <div class="flex flex-column gap-2 align-items-end">
        <div class="flex align-items-center gap-3">
          <div class="flex align-items-center gap-2">
            <p-checkbox
              [(ngModel)]="canSafelyDelete"
              [binary]="true"
              inputId="safe-delete"></p-checkbox>
            <label for="safe-delete" class="text-sm cursor-pointer"
              >Kann sicher gelöscht werden</label
            >
          </div>
          <div class="flex align-items-center gap-2">
            <p-checkbox
              [(ngModel)]="forceDelete"
              [binary]="true"
              inputId="force-delete"></p-checkbox>
            <label for="force-delete" class="text-sm cursor-pointer text-red-500"
              >Auch mit Daten löschen (Vorsicht!)</label
            >
          </div>
        </div>
        <p-button
          [rounded]="true"
          label="Doppelte Indikatoren ohne Daten löschen"
          icon="pi pi-trash"
          severity="danger"
          [disabled]="!canSafelyDelete()"
          (onClick)="deleteIndicatorsWithoutData()"></p-button>
      </div>
    </div>

    <!-- Summary -->
    <div class="mb-3">
      <p class="text-color-secondary">
        Es wurden <strong>{{ duplicateIndicators.length }}</strong> Indikatoren
        mit doppeltem Schlüssel gefunden.
        @if (filteredIndicators().length !== duplicateIndicators.length) {
          <span>
            ({{ filteredIndicators().length }} nach Filter angezeigt)
          </span>
        }
      </p>
    </div>

    <!-- Table -->
    <p-table
      [value]="groupedIndicators()"
      [columns]="cols"
      [paginator]="true"
      [rows]="10"
      [rowsPerPageOptions]="[10, 25, 50]"
      [tableStyle]="{ 'min-width': '50rem' }"
      styleClass="p-datatable-sm">
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th *ngFor="let col of columns">
            <span
              [class.justify-content-center]="!col.header"
              class="flex items-center gap-2 justify-content-center"
              [pTooltip]="col.tooltip"
              tooltipPosition="left">
              @if (col) {
                <i *ngIf="col.icon" [class]="col.icon"></i>
              }
              {{ col.header }}
            </span>
          </th>
          <th class="text-center"><i class="fas fa-trash"></i></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-group let-columns="columns">
        @if (group.set) {
          <!-- Set row -->
          <tr class="font-semibold bg-primary-50">
            <td>
              <i class="fas fa-ruler-combined"></i>
            </td>
            <td>
              <app-full-indicator-name
                [indicator]="group.set.indicator"
                mode="quiet" />
              <i class="text-color-secondary font-normal text-sm">
                {{ indicatorStore.numberOfChildren(group.set.indicator._id) }}
                Ausprägungen
              </i>
            </td>
            <td>
              <p-chip [label]="group.set.key" styleClass="text-xs"></p-chip>
            </td>
            <td class="w-2rem text-center">
              <p-tag
                [style]="{ 'min-width': '1.625rem' }"
                value="-"
                severity="secondary"
                [rounded]="true"></p-tag>
            </td>
            <td class="w-2rem text-center">
              @if (group.isParentProxy) {
                <span class="text-color-secondary">-</span>
              } @else {
                <p-tag
                  [style]="{ 'min-width': '1.625rem' }"
                  [value]="group.set.duplicateCount.toString()"
                  severity="warning"
                  [rounded]="true"></p-tag>
              }
            </td>
            <td class="w-2rem text-center">-</td>
            <td class="w-2rem text-center">
              @if (group.set.indicator) {
                <button
                  [rounded]="true"
                  severity="danger"
                  icon="fas fa-trash"
                  pButton
                  class="p-button-xs"
                  (click)="
                    confirmDeletion($event, group.set.indicator)
                  "></button>
              }
            </td>
          </tr>
          <!-- Children rows -->
          @for (child of group.children; track child.indicator._id) {
            <tr class="bg-primary-50">
              <td class="pl-5">
                <i class="fas fa-ruler text-sm"></i>
              </td>
              <td>
                <app-full-indicator-name
                  [indicator]="child.indicator"
                  mode="quiet" />
              </td>
              <td>
                <p-chip [label]="child.key" styleClass="text-xs"></p-chip>
              </td>
              <td class="w-2rem text-center">
                <p-tag
                  [style]="{ 'min-width': '1.625rem' }"
                  [value]="child.dataCount.toString()"
                  [severity]="child.dataCount === 0 ? 'danger' : 'success'"
                  [rounded]="true"></p-tag>
              </td>
              <td class="w-2rem text-center">
                <p-tag
                  [style]="{ 'min-width': '1.625rem' }"
                  [value]="child.duplicateCount.toString()"
                  severity="warning"
                  [rounded]="true"></p-tag>
              </td>
              <td class="w-2rem text-center">
                <p-tag
                  [style]="{ 'min-width': '1.625rem' }"
                  [value]="
                    bookmarkStore
                      .numberOfBookmarkReferencesByIndicatorId(
                        child.indicator._id
                      )
                      .toString()
                  "
                  severity="secondary"
                  [rounded]="true"></p-tag>
              </td>
              <td class="w-2rem text-center">
                <button
                  [rounded]="true"
                  severity="danger"
                  icon="fas fa-trash"
                  pButton
                  class="p-button-xs"
                  (click)="confirmDeletion($event, child.indicator)"></button>
              </td>
            </tr>
          }
        } @else if (group.standalone) {
          <!-- Standalone indicator row -->
          <tr>
            <td>
              <i
                class="fas"
                [class.fa-ruler]="group.standalone.indicator.parent"
                [class.fa-ruler-horizontal]="
                  !group.standalone.indicator.parent
                "></i>
            </td>
            <td>
              <app-full-indicator-name
                [indicator]="group.standalone.indicator"
                mode="quiet" />
            </td>
            <td>
              <p-chip
                [label]="group.standalone.key"
                styleClass="text-xs"></p-chip>
            </td>
            <td class="w-2rem text-center">
              <p-tag
                [style]="{ 'min-width': '1.625rem' }"
                [value]="group.standalone.dataCount.toString()"
                [severity]="
                  group.standalone.dataCount === 0 ? 'danger' : 'success'
                "
                [rounded]="true"></p-tag>
            </td>
            <td class="w-2rem text-center">
              <p-tag
                [style]="{ 'min-width': '1.625rem' }"
                [value]="group.standalone.duplicateCount.toString()"
                severity="warning"
                [rounded]="true"></p-tag>
            </td>
            <td class="w-2rem text-center">
              <p-tag
                [style]="{ 'min-width': '1.625rem' }"
                [value]="
                  bookmarkStore
                    .numberOfBookmarkReferencesByIndicatorId(
                      group.standalone.indicator._id
                    )
                    .toString()
                "
                severity="secondary"
                [rounded]="true"></p-tag>
            </td>
            <td class="w-2rem text-center">
              <button
                [rounded]="true"
                severity="danger"
                icon="fas fa-trash"
                pButton
                class="p-button-xs"
                (click)="
                  confirmDeletion($event, group.standalone.indicator)
                "></button>
            </td>
          </tr>
        }
      </ng-template>
    </p-table>
  }
</div>
